import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Tooltip } from '@mui/material';
import ReadMoreIcon from '@mui/icons-material/ReadMore';

import DictionaryFilter from '../../../components/DictionaryFilter';
import FunctionalTable from '../../../components/FunctionalTable';
import NowrapWrapper from '../../../components/NowrapWrapper';
import OverLayLoader from '../../../components/OverLayLoader';
import BulkKeywordReplacementDrawer from '../../../components/BulkKeywordReplacementDrawer';
import { EditableCell } from '../../../components/EditableCell';
import EditableSelectCell from '../../../components/EditableSelectCell';
import { SelectColumnFilter } from '../../../components/ColumnFilter';
import useTitle from '../../../hooks/useTitle';
import usePersistColumns from '../../../hooks/usePersistColumns';
import useDeleteRow from '../../../hooks/useDeleteRow';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DeleteRowButton from '../../../components/DeleteRowButton';
import AddKeywordButton from '../../../components/EditableCell/AddKeywordButton';
import { languageOption, entireRowModeOptions } from '../../../helpers/options';
import { CONSTANT_KEYS } from '../../../helpers/tableKeys';
import PATH from '../../../helpers/routes.json';
import saveFile from '../../../utils/saveFile';
import {
  bulkUpdateWordSet,
  deleteWordSet,
  downloadWordCodes,
  getLanguages,
  getWordCodes,
  transferWords,
  updateWordSet,
} from '../../../services';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import { useAuth } from '../../../context/auth/AuthContext';

const { CODE, CODING, NAME, SAMPLE, WORDSET, WORDSET_EXACT, ENTIRE_ROW } =
  CONSTANT_KEYS;

const optionalWordsColumns = [NAME, CODING, ENTIRE_ROW];

const BULK_REPLACEMENT_PARAM = 'bulk_replacement';
const BULK_REPLACED_DEBOUNCE_MS = 400;

const initialFilter = {
  language: null,
  additions: [NAME, ENTIRE_ROW],
};

const WordsSettingsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState(initialFilter);
  const [data, setData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [languageList, setLanguageList] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [isLanguageLoadFailed, setIsLanguageLoadFailed] = useState(false);
  const [isSearchParamsLoading, setIsSearchParamsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);
  const [isBulkReplaceOpen, setIsBulkReplaceOpen] = useState(false);

  const {
    isDeleting,
    itemToDelete: wordSetToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
  } = useDeleteRow({
    onDelete: item => deleteWordSet(item.id),
    onSuccess: () => setFetchKey(prev => prev + 1),
  });
  const bulkReplacedDebounceRef = useRef(null);
  const activeFetchRequestsRef = useRef(0);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { t } = useTranslation([
    'common',
    'promptbar',
    'filter',
    'words_settings',
  ]);

  const selectedLanguage = filter.language || languageOption[0];
  const isBlockingBusy =
    isSaving || isDownloading || isSearchParamsLoading || isBulkUpdating;
  const isPageBusy = isFetching || isBlockingBusy;
  const shouldShowOverlay =
    isOptionsLoading || isBlockingBusy || (isFetching && !isBulkReplaceOpen);
  const rowBackgrounds = useMemo(() => {
    let previousCode;
    let useSecondaryBackground = false;

    return data.map(row => {
      if (row.code !== previousCode) {
        useSecondaryBackground = !useSecondaryBackground;
        previousCode = row.code;
      }

      return useSecondaryBackground
        ? 'var(--color-bg-input)'
        : 'var(--color-white)';
    });
  }, [data]);
  const getRowSx = useCallback(
    (_, index) => ({ backgroundColor: rowBackgrounds[index] }),
    [rowBackgrounds],
  );

  const title = t('common:pages.dictionaries.tabs.words');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);
  usePersistColumns('wordsSettingsColumns', ['additions']);

  const buildWordCodesParams = useCallback(
    ({ params, languageId, withPagination = true }) => ({
      ...(withPagination
        ? {
            limit: params.limit,
            offset: params.offset,
          }
        : {}),
      language_id: languageId,
      ...(params[CODE]
        ? {
            code: params[CODE],
          }
        : {}),
      ...(params[CODING]
        ? {
            coding: params[CODING],
          }
        : {}),
      ...(params[NAME]
        ? {
            code_name: params[NAME],
          }
        : {}),
      ...(params[WORDSET]
        ? {
            wordset: params[WORDSET],
          }
        : {}),
      ...(params[WORDSET_EXACT]
        ? {
            wordset_exact: params[WORDSET_EXACT],
          }
        : {}),
      ...(params[SAMPLE]
        ? {
            sample: params[SAMPLE],
          }
        : {}),
      ...(params[ENTIRE_ROW] ? { entire_row: params[ENTIRE_ROW] } : {}),
    }),
    [],
  );

  useEffect(() => {
    if (isOptionsLoading) {
      return;
    }

    if (
      searchParams.has('additions') &&
      !searchParams.get('additions')?.length
    ) {
      setFilter(prev => ({ ...prev, additions: [] }));
    } else if (
      searchParams.has('additions') &&
      searchParams.get('additions')?.length
    ) {
      const additions = searchParams.get('additions').split(',');
      setFilter(prev => ({ ...prev, additions }));
    } else {
      searchParams.set('additions', initialFilter.additions.join(','));
      setFilter(prev => ({ ...prev, additions: initialFilter.additions }));
    }

    setSearchParams(searchParams, { replace: true });
    setIsSearchParamsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoading]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchLanguages = async () => {
      try {
        const result = await getLanguages();

        if (!Array.isArray(result?.data) || !result.data.length) {
          throw new Error('Languages are unavailable');
        }

        const defaultLang =
          result.data.find(el => el.id === user.data_language_id)?.name ||
          languageOption[0];

        setLanguageList(result.data);
        setFilter(prev => ({ ...prev, language: defaultLang }));
        setIsLanguageLoadFailed(false);
      } catch (error) {
        setLanguageList([]);
        setFilter(prev => ({ ...prev, language: languageOption[0] }));
        setIsLanguageLoadFailed(true);
        createErrorNotification();
        console.log(error.message);
      } finally {
        setIsOptionsLoading(false);
      }
    };

    fetchLanguages();
  }, [user]);

  useEffect(
    () => () => {
      if (bulkReplacedDebounceRef.current) {
        clearTimeout(bulkReplacedDebounceRef.current);
      }
    },
    [],
  );

  const fetchData = useCallback(
    async ({ params, signal }) => {
      if (!languageList) {
        return;
      }

      const currentLanguageID = languageList.find(
        lang => lang.name === params.globalFilter,
      )?.id;

      if (!currentLanguageID) {
        return;
      }

      activeFetchRequestsRef.current += 1;
      setIsFetching(true);

      const newParams = buildWordCodesParams({
        params,
        languageId: currentLanguageID,
      });

      try {
        const result = await getWordCodes(newParams, signal);
        if (result) {
          const newData = result.data.map(el => ({
            ...el,
            id: el.wordset_code_id,
            uniqId: el.wordset_code_id,
            word_id: el.wordset_id,
            name: el.code_name,
          }));

          setData(prev =>
            params.offset === 0 ? [...newData] : [...prev, ...newData],
          );
          setHasMoreData(prev => (params.offset === 0 ? true : prev));
          if (result.data.length < params.limit) {
            setHasMoreData(false);
          }
        }
      } catch (error) {
        createErrorNotification();
        console.log(error.message);
      } finally {
        activeFetchRequestsRef.current = Math.max(
          0,
          activeFetchRequestsRef.current - 1,
        );

        if (activeFetchRequestsRef.current === 0) {
          setIsFetching(false);
        }
      }
    },
    [buildWordCodesParams, languageList],
  );

  const renderAddKeywordButton = useCallback(
    row => {
      const codeId = row?.code_id;

      if (!codeId) {
        return null;
      }

      const currentLanguageId = languageList?.find(
        lang => lang.name === selectedLanguage,
      )?.id;

      const names = Array.isArray(row?.names)
        ? row.names
        : currentLanguageId
          ? [{ language_id: currentLanguageId, name: row?.name || '' }]
          : [];

      return (
        <AddKeywordButton
          to={`${PATH.dictionaries.addWords}/${codeId}/${selectedLanguage}?${new URLSearchParams(
            {
              code: String(row.code),
              name: row.name || '',
            },
          )}`}
          state={{
            language: selectedLanguage,
            from: location,
            codeInfo: {
              ...row,
              names,
            },
          }}
          title={t('promptbar:addKeywords')}
        />
      );
    },
    [languageList, location, selectedLanguage, t],
  );

  const handleFilterSelect = event => {
    const { name, value } = event.target;

    setFilter(prev => ({ ...prev, [name]: value }));

    if (name !== 'language') {
      searchParams.set(name, value);
      setSearchParams(searchParams);
    }
  };

  const handleFilterReset = () => {
    navigate(location.pathname, { replace: true });
    setTimeout(() => navigate(0));
  };

  const editHandler = rowId => {
    setData(prev =>
      prev.map(row =>
        row.id === rowId ? { ...row, isEdit: true, prevState: row } : row,
      ),
    );
  };

  const handleChangeCodeData = useCallback(
    ({ index, value, columnName, isValid }) => {
      setData(prev =>
        prev.map(row =>
          row.id === index ? { ...row, [columnName]: value, isValid } : row,
        ),
      );
    },
    [],
  );

  const applyChangesHandler = async (rowId, data) => {
    if (data?.isValid === false) {
      return;
    }

    const isCodeChanged = data?.prevState?.[CODE] !== data?.[CODE];
    const isSampleChanged = data?.prevState?.[SAMPLE] !== data?.[SAMPLE];
    const isWordsetChanged = data?.prevState?.[WORDSET] !== data?.[WORDSET];
    const isEntireRowChanged =
      data?.prevState?.[ENTIRE_ROW] !== data?.[ENTIRE_ROW];

    if (
      !isCodeChanged &&
      !isSampleChanged &&
      !isWordsetChanged &&
      !isEntireRowChanged
    ) {
      setData(prev =>
        prev.map(row => {
          if (row.id !== rowId) {
            return row;
          }

          return {
            ...row,
            isEdit: false,
            prevState: undefined,
            isValid: undefined,
          };
        }),
      );

      return;
    }

    let hasSuccessfulMutation = false;
    let failedError = null;

    try {
      setIsSaving(true);

      if (isSampleChanged || isWordsetChanged || isEntireRowChanged) {
        try {
          await updateWordSet(data.word_id, {
            wordset: data.wordset || '',
            sample: data.sample || '',
            ...(isEntireRowChanged ? { entire_row: data[ENTIRE_ROW] } : {}),
          });
          hasSuccessfulMutation = true;
        } catch (error) {
          failedError = error;
        }
      }

      if (isCodeChanged) {
        try {
          await transferWords({
            old_code: data.prevState.code,
            new_code: data.code,
            word_id: data.word_id,
          });
          hasSuccessfulMutation = true;
        } catch (error) {
          failedError = failedError || error;
        }
      }

      if (hasSuccessfulMutation) {
        setFetchKey(prev => prev + 1);
      }

      if (failedError) {
        if (
          failedError?.response?.status === 422 &&
          failedError?.response?.data?.detail
        ) {
          createErrorNotification(failedError.response.data.detail);
        } else {
          createErrorNotification();
        }

        console.log(failedError.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const cancelChangesHandler = rowId => {
    setData(prev =>
      prev.map(row => (row.id === rowId ? { ...row.prevState } : row)),
    );
  };

  const handleDownload = async () => {
    if (!selectedLanguage || !languageList?.length) {
      createErrorNotification();
      return;
    }

    try {
      setIsDownloading(true);

      const currentLanguageID = languageList.find(
        lang => lang.name === selectedLanguage,
      )?.id;

      const params = {
        [CODE]: searchParams.get(CODE) || '',
        [CODING]: searchParams.get(CODING) || '',
        [NAME]: searchParams.get(NAME) || '',
        [WORDSET]: searchParams.get(WORDSET) || '',
        [WORDSET_EXACT]: searchParams.get(WORDSET_EXACT) || '',
        [SAMPLE]: searchParams.get(SAMPLE) || '',
        [ENTIRE_ROW]: searchParams.get(ENTIRE_ROW) || '',
      };

      const downloadParams = buildWordCodesParams({
        params,
        languageId: currentLanguageID,
        withPagination: false,
      });

      const result = await downloadWordCodes(downloadParams);

      if (result) {
        const blob = new Blob([result.data], {
          type: 'application/octet-stream',
        });
        saveFile(blob, `words_${selectedLanguage}.xlsx`);
      }
    } catch (error) {
      createErrorNotification();
      console.log(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const openBulkReplaceDrawer = () => {
    setIsBulkReplaceOpen(true);
  };

  const closeBulkReplaceDrawer = () => {
    if (bulkReplacedDebounceRef.current) {
      clearTimeout(bulkReplacedDebounceRef.current);
      bulkReplacedDebounceRef.current = null;
    }

    setIsBulkReplaceOpen(false);
  };

  const cancelBulkReplaceDrawer = () => {
    if (bulkReplacedDebounceRef.current) {
      clearTimeout(bulkReplacedDebounceRef.current);
      bulkReplacedDebounceRef.current = null;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(WORDSET_EXACT);
    nextParams.delete(BULK_REPLACEMENT_PARAM);

    setSearchParams(nextParams, { replace: true });
    setIsBulkReplaceOpen(false);
  };

  const handleBulkReplacedChange = useCallback(
    replacedValue => {
      if (bulkReplacedDebounceRef.current) {
        clearTimeout(bulkReplacedDebounceRef.current);
      }

      bulkReplacedDebounceRef.current = setTimeout(() => {
        const nextParams = new URLSearchParams(searchParams);

        if (replacedValue) {
          nextParams.set(WORDSET_EXACT, replacedValue);
        } else {
          nextParams.delete(WORDSET_EXACT);
        }

        setSearchParams(nextParams, { replace: true });
      }, BULK_REPLACED_DEBOUNCE_MS);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyBulkReplace = useCallback(
    async values => {
      if (bulkReplacedDebounceRef.current) {
        clearTimeout(bulkReplacedDebounceRef.current);
        bulkReplacedDebounceRef.current = null;
      }

      if (!selectedLanguage || !languageList?.length) {
        createErrorNotification();
        return false;
      }

      const currentLanguageID = languageList.find(
        lang => lang.name === selectedLanguage,
      )?.id;

      if (!currentLanguageID) {
        createErrorNotification();
        return false;
      }

      const replacedValue = values.replaced?.trim() || null;
      const replacementValue = values.replacement?.trim() || null;

      const filters = {
        language_id: currentLanguageID,
        code: searchParams.get(CODE) || null,
        coding: searchParams.get(CODING) || null,
        code_name: searchParams.get(NAME) || null,
        wordset: searchParams.get(WORDSET) || null,
        wordset_exact: searchParams.get(WORDSET_EXACT) || replacedValue,
        sample: searchParams.get(SAMPLE) || null,
      };

      try {
        setIsBulkUpdating(true);

        await bulkUpdateWordSet({
          filters,
          new_wordset: replacementValue,
        });

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(WORDSET_EXACT, replacementValue);
        nextParams.delete(BULK_REPLACEMENT_PARAM);

        setSearchParams(nextParams);
        setFetchKey(prev => prev + 1);

        return true;
      } catch (error) {
        if (error?.response?.status === 422 && error?.response?.data?.detail) {
          createErrorNotification(error.response.data.detail);
        } else {
          createErrorNotification();
        }

        console.log(error.message);

        return false;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    [
      selectedLanguage,
      languageList,
      searchParams,
      setSearchParams,
      setFetchKey,
    ],
  );

  const deleteActionComponent = useCallback(
    row => {
      if (row.isEdit || !row.word_id) {
        return null;
      }

      return (
        <DeleteRowButton
          disabled={isPageBusy || isDeleting}
          onClick={() =>
            openDeleteDialog({
              id: row.word_id,
              code: row.code,
              wordset: row.wordset,
            })
          }
        />
      );
    },
    [isDeleting, isPageBusy, openDeleteDialog],
  );

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t('words_settings:table.code')} />,
        id: CODE,
        accessor: CODE,
        style: { width: '12%', maxWidth: '130px', minWidth: '120px' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          if (!isEdit) {
            return value || '-';
          }

          return (
            <EditableCell
              value={value}
              cell={cell}
              onChangeData={handleChangeCodeData}
              pathname={location.pathname}
            />
          );
        },
      },
      {
        Header: <NowrapWrapper title={t('words_settings:table.coding')} />,
        id: CODING,
        accessor: CODING,
        show: filter.additions.includes(CODING),
        style: { width: '10%', maxWidth: '130px', minWidth: '110px' },
      },
      {
        Header: (
          <NowrapWrapper
            title={t(`words_settings:table.name_${selectedLanguage}`)}
          />
        ),
        id: NAME,
        accessor: NAME,
        show: filter.additions.includes(NAME),
        style: { width: '26%' },
      },
      {
        Header: (
          <NowrapWrapper
            title={t(`words_settings:table.sample_${selectedLanguage}`)}
          />
        ),
        id: SAMPLE,
        accessor: SAMPLE,
        style: { width: '20%' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          if (!isEdit) {
            return value || '-';
          }

          return (
            <EditableCell
              value={value}
              cell={cell}
              onChangeData={handleChangeCodeData}
              pathname={location.pathname}
            />
          );
        },
      },
      {
        Header: (
          <NowrapWrapper
            title={t(`words_settings:table.words_${selectedLanguage}`)}
          />
        ),
        id: WORDSET,
        accessor: WORDSET,
        style: { width: '20%' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          if (!isEdit) {
            return value || '-';
          }

          return (
            <EditableCell
              value={value}
              cell={cell}
              onChangeData={handleChangeCodeData}
              pathname={location.pathname}
            />
          );
        },
      },
      {
        Header: <NowrapWrapper title={t('words_settings:table.search')} />,
        id: ENTIRE_ROW,
        accessor: ENTIRE_ROW,
        show: filter.additions.includes(ENTIRE_ROW),
        style: { width: '8%', minWidth: '90px', maxWidth: '120px' },
        Filter: cell => (
          <SelectColumnFilter cell={cell} options={entireRowModeOptions} />
        ),
        Cell: ({ value, ...cell }) => {
          const { isEdit, uniqId } = cell.row.original;

          if (!isEdit) {
            return value || '-';
          }

          return (
            <EditableSelectCell
              value={value}
              isEdit={isEdit}
              rowIndex={cell.row.index}
              rowUniqId={uniqId}
              columnName={ENTIRE_ROW}
              options={entireRowModeOptions}
              onChangeData={handleChangeCodeData}
            />
          );
        },
      },
      {
        Header: '',
        id: 'add-word',
        disableFilters: true,
        className: 'center',
        style: { width: '2%', minWidth: '44px', maxWidth: '44px' },
        Cell: ({ row: { original } }) => renderAddKeywordButton(original),
      },
    ],
    // deleteActionComponent included to invalidate columns ref when delete button state changes —
    // required to prevent stale closure in react-table v7 visibleColumns hooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filter.additions,
      handleChangeCodeData,
      location.pathname,
      t,
      selectedLanguage,
      deleteActionComponent,
      renderAddKeywordButton,
    ],
  );

  return (
    <>
      {shouldShowOverlay && <OverLayLoader />}

      {!isOptionsLoading && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <DictionaryFilter
              multipleLanguage={false}
              filter={filter}
              options={optionalWordsColumns}
              onFilterReset={handleFilterReset}
              onFilterSelect={handleFilterSelect}
              onDownload={handleDownload}
            />

            <Tooltip title={t('words_settings:bulkReplace.title')} arrow>
              <span>
                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  onClick={openBulkReplaceDrawer}
                  disabled={isBlockingBusy}
                >
                  <ReadMoreIcon />
                </Button>
              </span>
            </Tooltip>
          </Box>

          {!isSearchParamsLoading && !isLanguageLoadFailed && (
            <FunctionalTable
              key={`${fetchKey}-${searchParams.get(WORDSET_EXACT) || ''}`}
              type="sticky"
              columns={columns}
              data={data}
              fetchData={fetchData}
              onEdit={editHandler}
              applyChanges={applyChangesHandler}
              cancelChanges={cancelChangesHandler}
              hasMoreData={hasMoreData}
              isInfiniteScroll
              pageSize={50}
              globalFilter={filter.language}
              actionColumnComponent={deleteActionComponent}
              getRowSx={getRowSx}
            />
          )}
        </Box>
      )}

      <BulkKeywordReplacementDrawer
        open={isBulkReplaceOpen}
        onClose={closeBulkReplaceDrawer}
        onCancel={cancelBulkReplaceDrawer}
        onApply={handleApplyBulkReplace}
        onReplacedChange={handleBulkReplacedChange}
        disabled={isBlockingBusy}
        defaultValues={{
          replaced: searchParams.get(WORDSET_EXACT) || '',
          replacement: searchParams.get(BULK_REPLACEMENT_PARAM) || '',
        }}
      />

      <DeleteConfirmDialog
        open={Boolean(wordSetToDelete)}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title={t('words_settings:deleteDialog.title')}
        description={t('words_settings:deleteDialog.description', {
          code: wordSetToDelete?.code || '-',
        })}
      />
    </>
  );
};

export default WordsSettingsView;
